import React, { useState, useEffect } from "react";
import { Container, Row, Col, Form, Button, Table, Modal, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { FaEye, FaMapMarkerAlt } from "react-icons/fa";
import { attendanceService } from "../services/attendanceService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const StaffMonthlyAttendanceScreen = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [staffList, setStaffList] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);

  const { userInfo } = useSelector((state) => state.auth);
  const ReqCompanyCode = userInfo && userInfo[13]?.InfoField;
  const StaffId = userInfo && userInfo[1]?.InfoField;

  const fetchData = async () => {
    setLoading(true);
    try {
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const year = selectedDate.getFullYear();
      const empCode = userInfo && userInfo[1]?.InfoField;
      const companyCode = userInfo && userInfo[13]?.InfoField;
      const data = await attendanceService.getEmployeeMonthlyAttList(month, year, empCode, companyCode);
      setStaffList(data);
    } catch (error) {
      console.error("Error fetching attendance:", error);
      toast.error("Failed to fetch attendance data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedDate]);

  const openMapWithLatLng = (latlng) => {
    if (!latlng) return;
    const [latitude, longitude] = latlng.split(',').map(coord => coord.trim());
    const url = `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
    window.open(url, '_blank');
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  return (
    <Container className="py-4">
      <Row className="mb-3 align-items-center justify-content-between">
        <Col xs={12} md={6}>
          <h4 className="text-center">Monthly Attendance</h4>
        </Col>
        <Col xs={12} md="auto" className="d-flex gap-2 mt-2 mt-md-0">
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            dateFormat="MM/yyyy"
            showMonthYearPicker
            className="form-control"
          />
          <Button variant="primary" onClick={fetchData}>
            Refresh
          </Button>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : staffList.length === 0 ? (
        <div className="text-center py-5">
          <p>No data available</p>
        </div>
      ) : (
        <div className="table-responsive">
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Staff ID</th>
                <th>Name</th>
                <th>Type</th>
                <th>Entry Date</th>
                <th>Entry Time</th>
                <th>Role</th>
                <th>View</th>
              </tr>
            </thead>
            <tbody>
              {staffList.map((staff, index) => (
                <tr key={staff.AttID || staff.EMPCODE + staff.EntryTime + index}>
                  <td className="text-center">{index + 1}</td>
                  <td className="text-center">{staff.EMPCODE}</td>
                  <td className="text-center">{staff.EmployeeName}</td>
                  <td className="text-center">{staff.AttType}</td>
                  <td className="text-center">{staff.AttDate ? new Date(staff.AttDate).toLocaleDateString('en-GB') : ''}</td>
                  <td className="text-center">{staff.EntryTime}</td>
                  <td className="text-center">{staff.Designation}</td>
                  <td className="text-center">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        setSelectedMember(staff);
                        setShowModal(true);
                      }}
                    >
                      <FaEye />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Attendance Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedMember && (
            <div>
              <Row>
                <Col md={6}>
                  <h5>Employee Information</h5>
                  <p><strong>Name:</strong> {selectedMember.EmployeeName}</p>
                  <p><strong>ID:</strong> {selectedMember.EMPCODE}</p>
                  <p><strong>Type:</strong> {selectedMember.AttType}</p>
                  <p><strong>Entry Date:</strong> {selectedMember.AttDate ? new Date(selectedMember.AttDate).toLocaleDateString('en-GB') : ''}</p>
                  <p><strong>Entry Time:</strong> {selectedMember.EntryTime}</p>
                  <p><strong>Department:</strong> {selectedMember.Department}</p>
                  <p><strong>Designation:</strong> {selectedMember.Designation}</p>
                  <p><strong>Work Hours:</strong> {selectedMember.WorkHours}</p>
                  <p><strong>Work Remarks:</strong> {selectedMember.WorkRemarks}</p>
                  {selectedMember.AttLocation && (
                    <p>
                      <strong>Location:</strong>{" "}
                      <Button
                        variant="link"
                        className="p-0"
                        onClick={() => openMapWithLatLng(selectedMember.AttLocation)}
                      >
                        <FaMapMarkerAlt className="text-danger me-1" />
                        View on Map
                      </Button>
                    </p>
                  )}
                </Col>
                <Col md={6}>
                  {selectedMember.AttImage && (
                    <img
                      src={`${process.env.REACT_APP_API_URL}${selectedMember.AttImage}`}
                      alt="Attendance"
                      className="img-fluid rounded"
                    />
                  )}
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default StaffMonthlyAttendanceScreen; 